import React from 'react';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import {
  selectLicenseInfo,
  dataForLicenseDisplay,
} from 'software_renewal/redux/selectors';

/**
 * Component that displays a notice when a serial should have a complimentary serial
 * but one wasn't created (e.g., blacklisted serials).
 * Only renders if:
 * - Product is ViewMate Pro (or other eligible products)
 * - Expiration date hasn't passed
 * - complementedBy is null/undefined
 */
const EligibleComplimentarySerialNotice = () => {
  const licenseInfo = useSelector(selectLicenseInfo);
  const displayLicenseInfo = useSelector(dataForLicenseDisplay);
  const { products } = displayLicenseInfo || {};

  // Must have license info and products
  if (!licenseInfo || !products?.length) {
    return null;
  }

  const { expirationDate, complementedBy, serialNumber } = licenseInfo;

  // Check if complementedBy is null/undefined (not present)
  const hasComplimentarySerial =
    complementedBy != null && typeof complementedBy === 'object';

  // If they already have a complimentary serial, don't show this notice
  if (hasComplimentarySerial) {
    return null;
  }

  // Check if product is ViewMate Pro (eligible for complimentary serial)
  const isViewMatePro = products.some(
    ({ product }) => product.name === 'ViewMate Pro',
  );

  // If not ViewMate Pro, don't show notice
  if (!isViewMatePro) {
    return null;
  }

  // Check if expiration date hasn't passed
  if (!expirationDate) {
    return null;
  }

  const expired = dayjs().isAfter(dayjs(expirationDate));
  if (expired) {
    return null;
  }

  // Build message for contact form
  const message = `Hello,\n\nI am eligible for a complimentary ViewMate Professional serial number based on my ViewMate Pro license (Serial: ${serialNumber}), but I was unable to receive it automatically.\n\nCould you please help me claim my complimentary serial?\n\nThank you.`;
  const contactUsLink = `/complimentary-serial-request?msg=${encodeURIComponent(message)}`;

  return (
    <div className="card product-listing p-4 mb-3">
      <div className="row">
        <div className="col-12">
          <h4 className="font-heavy text-uppercase mb-3 title">
            Eligible for Complimentary Serial
          </h4>
          <p className="mb-3">
            You are eligible for a complimentary serial but we could not
            create it automatically. Please{' '}
            <a href={contactUsLink} className="alert-link">
              contact support
            </a>{' '}
            so you can claim one.
          </p>
          <p className="mb-0">
            <a
              href={contactUsLink}
              className="btn btn-primary"
            >
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default EligibleComplimentarySerialNotice;

